import { useEffect, useState, useCallback } from "react";
import { Loader2, Wallet, Badge as BadgeIcon, FileText, RefreshCw } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ExportMenu from "@/components/common/ExportMenu";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";
import { useToast } from "@/hooks/use-toast";
import MetaSpendCard from "@/components/admin/MetaSpendCard";

export default function Donations() {
  const [items, setItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [unattributedOnly, setUnattributedOnly] = useState(false);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [assigningId, setAssigningId] = useState(null);
  const [assignCaseNumber, setAssignCaseNumber] = useState("");
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcilePaymentId, setReconcilePaymentId] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const { toast } = useToast();

  const buildFilterParams = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (unattributedOnly) params.unattributed = true;
    if (campaignFilter) params.campaign = campaignFilter;
    return params;
  }, [search, startDate, endDate, unattributedOnly, campaignFilter]);

  const fetchDonations = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/donations", { params: buildFilterParams() });
    setItems(data.items);
    setTotalAmount(data.total_amount);
    setLoading(false);
  }, [buildFilterParams]);

  useEffect(() => {
    fetchDonations();
  }, [fetchDonations]);

  const handleAssign = async (donationId) => {
    if (!assignCaseNumber) return;
    try {
      await api.patch(`/donations/${donationId}/assign-case`, { case_number: parseInt(assignCaseNumber, 10) });
      toast({ title: "Donation assigned" });
      setAssigningId(null);
      setAssignCaseNumber("");
      fetchDonations();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not assign", description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const handleDownloadReceipt = async (donationId) => {
    try {
      const response = await api.get(`/donations/${donationId}/receipt.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${donationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ variant: "destructive", title: "Could not download receipt" });
    }
  };

  const handleReconcile = async () => {
    if (!reconcilePaymentId.trim()) return;
    setReconciling(true);
    try {
      const { data } = await api.post("/donations/reconcile", { razorpay_payment_id: reconcilePaymentId.trim() });
      toast({ title: data.already_existed ? "Already recorded" : "Reconciled", description: data.message });
      if (!data.already_existed) {
        setReconcileOpen(false);
        setReconcilePaymentId("");
        fetchDonations();
      }
    } catch (err) {
      toast({ variant: "destructive", title: "Could not reconcile", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setReconciling(false);
    }
  };

  const handleMarkTest = async (donationId) => {
    try {
      await api.patch(`/donations/${donationId}/mark-test`);
      toast({ title: "Marked as test" });
      fetchDonations();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not mark as test", description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="font-display text-3xl text-amtm-text" data-testid="donations-page-title">Donations</h1>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setReconcileOpen(true)}
              data-testid="donations-reconcile-button"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Reconcile from Razorpay
            </Button>
            <ExportMenu url="/donations/export" params={buildFilterParams()} filename="amtm_donations" testId="donations-export-button" />
          </div>
        </div>

        <MetaSpendCard testId="donations-meta-spend-card" />

        <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
          <DialogContent data-testid="reconcile-donation-dialog">
            <DialogHeader>
              <DialogTitle>Reconcile a donation from Razorpay</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-amtm-textMuted">
              Use this if a payment captured on Razorpay but its receipt never sent (e.g. a webhook secret was
              misconfigured). Paste the Razorpay Payment ID — we'll fetch it, backfill the donation record, and
              email the donor their 80G receipt. Safe to retry: already-recorded payments won't be duplicated.
            </p>
            <div>
              <Label htmlFor="reconcile-payment-id">Razorpay Payment ID</Label>
              <Input
                id="reconcile-payment-id"
                placeholder="pay_XXXXXXXXXXXXXX"
                value={reconcilePaymentId}
                onChange={(e) => setReconcilePaymentId(e.target.value)}
                className="mt-1.5"
                data-testid="reconcile-payment-id-input"
              />
            </div>
            <Button onClick={handleReconcile} disabled={reconciling || !reconcilePaymentId.trim()} data-testid="reconcile-submit-button">
              {reconciling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Reconcile & Send Receipt
            </Button>
          </DialogContent>
        </Dialog>

        <div className="flex flex-wrap gap-3 mb-4">
          <div>
            <Label htmlFor="donation-search">Search animal / case #</Label>
            <Input id="donation-search" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="donations-search-input" className="mt-1.5 h-10 w-48" />
          </div>
          <div>
            <Label htmlFor="donation-start">From</Label>
            <Input id="donation-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="donations-start-date-input" className="mt-1.5 h-10" />
          </div>
          <div>
            <Label htmlFor="donation-end">To</Label>
            <Input id="donation-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="donations-end-date-input" className="mt-1.5 h-10" />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              variant={unattributedOnly ? "default" : "outline"}
              onClick={() => setUnattributedOnly((v) => !v)}
              data-testid="donations-unattributed-toggle"
              className={unattributedOnly ? "bg-amtm-teal text-amtm-text h-10 hover:bg-amtm-teal/90" : "h-10"}
            >
              Unattributed only
            </Button>
          </div>
          <div>
            <Label htmlFor="donation-campaign">Campaign</Label>
            <select
              id="donation-campaign"
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
              data-testid="donations-campaign-filter"
              className="mt-1.5 h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All campaigns</option>
              <option value="diwali2026">Diwali 2026</option>
            </select>
          </div>
        </div>

        <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4 mb-4 flex items-center gap-2" data-testid="donations-total-amount">
          <Wallet className="h-5 w-5 text-amtm-teal" />
          <span className="font-semibold text-lg text-amtm-text">₹{totalAmount.toLocaleString("en-IN")}</span>
          <span className="text-sm text-amtm-textMuted">total for current filter ({items.length} donations)</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
        ) : items.length === 0 ? (
          <p className="text-amtm-textMuted text-center py-12" data-testid="donations-empty-state">No donations found for this filter.</p>
        ) : (
          <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border" data-testid="donations-list">
            {items.map((d) => (
              <div key={d.id} className="flex items-center justify-between p-4 gap-3 flex-wrap" data-testid={`donation-item-${d.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-amtm-text flex items-center gap-2">
                    {d.animal_name ? (
                      `#${d.case_number} ${d.animal_name}`
                    ) : (
                      <Badge variant="outline" data-testid={`donation-unattributed-badge-${d.id}`}>
                        <BadgeIcon className="h-3 w-3 mr-1" /> Unattributed
                      </Badge>
                    )}
                    {d.is_test && (
                      <Badge variant="destructive" data-testid={`donation-test-badge-${d.id}`}>TEST</Badge>
                    )}
                  </p>
                  <p className="text-sm text-amtm-textMuted">{d.donor_name || "Anonymous"} · {formatDate(d.created_at)}</p>
                  {d.donor_email && <p className="text-xs text-amtm-textMuted" data-testid={`donation-email-${d.id}`}>{d.donor_email}</p>}
                  {d.donor_pan && <p className="text-xs text-amtm-textMuted" data-testid={`donation-pan-${d.id}`}>PAN: {d.donor_pan}</p>}
                  <p className="text-xs text-amtm-textMuted">Payment ID: {d.razorpay_payment_id}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <p className="font-semibold text-amtm-success">₹{d.amount.toLocaleString("en-IN")}</p>
                  {d.receipt_number && (
                    <Button size="sm" variant="outline" onClick={() => handleDownloadReceipt(d.id)} data-testid={`download-receipt-button-${d.id}`}>
                      <FileText className="h-4 w-4 mr-1.5" /> Receipt
                    </Button>
                  )}
                  {!d.case_id &&
                    (assigningId === d.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Case #"
                          type="number"
                          value={assignCaseNumber}
                          onChange={(e) => setAssignCaseNumber(e.target.value)}
                          className="h-9 w-24"
                          data-testid={`assign-case-number-input-${d.id}`}
                        />
                        <Button size="sm" onClick={() => handleAssign(d.id)} data-testid={`assign-case-confirm-${d.id}`}>Assign</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setAssigningId(d.id)} data-testid={`assign-case-button-${d.id}`}>
                        Assign to case
                      </Button>
                    ))}
                  {!d.is_test && (
                    <Button size="sm" variant="outline" onClick={() => handleMarkTest(d.id)} data-testid={`mark-test-button-${d.id}`}>
                      Mark as test
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
